import React, { useEffect, useState } from "react";
import { useProperty } from "@/context/PropertyContext";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { TrendingUp, Receipt, Calculator, Printer } from "lucide-react";
import { eur } from "@/lib/format";
import { MESES_NOMBRE } from "@/lib/format";

export default function Irpf() {
  const { selectedId, selected } = useProperty();
  const year = new Date().getFullYear();
  const years = [year, year - 1, year - 2, year - 3, year - 4];
  const [anio, setAnio] = useState(String(year - 1));
  const [modo, setModo] = useState("50");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openTenants, setOpenTenants] = useState([]);
  const declarationFactor = Number(modo) / 100;

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    api.get(`/irpf?property_id=${selectedId}&anio=${anio}&modo=${modo}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [selectedId, anio, modo]);

  const allTenantIds = (data?.detalle || []).map((d) => d.tenant_id);
  const allExpanded = allTenantIds.length > 0 && allTenantIds.every((id) => openTenants.includes(id));
  const toggleAll = () => setOpenTenants(allExpanded ? [] : allTenantIds);
  const toggleTenant = (id) => setOpenTenants((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const printIrpf = () => {
    if (!data) return;
    const pct = `${modo}%`;
    const w = window.open("", "_blank");
    w.document.write(`
      <!DOCTYPE html>
      <html>
      <head><meta charset="utf-8"><title>IRPF · ${selected?.nombre || ""} · ${anio}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', system-ui, sans-serif; font-size: 13px; color: #111; padding: 32px; }
        h1 { font-size: 20px; margin-bottom: 4px; }
        .sub { color: #666; margin-bottom: 20px; font-size: 14px; }
        .stats { display: flex; gap: 16px; margin-bottom: 24px; }
        .stat { flex: 1; border: 1px solid #ddd; border-radius: 6px; padding: 12px 14px; }
        .stat-label { font-size: 11px; text-transform: uppercase; letter-spacing: .08em; color: #888; margin-bottom: 4px; }
        .stat-value { font-size: 16px; font-weight: 700; }
        .stat-sub { font-size: 11px; color: #999; margin-top: 2px; }
        h2 { font-size: 16px; margin-bottom: 12px; margin-top: 8px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #eee; font-size: 12px; }
        th { background: #f5f5f5; font-weight: 600; }
        .r { text-align: right; }
        .m { font-family: 'Consolas', 'Courier New', monospace; }
        .month-detail { background: #fafafa; }
        .month-detail td { padding: 3px 8px; font-size: 11px; }
        .month-detail table { margin: 4px 0; }
        .month-detail th { background: #eee; font-size: 10px; }
        .total-row td { font-weight: 700; border-top: 2px solid #333; padding-top: 8px; }
        .footer { margin-top: 24px; font-size: 10px; color: #aaa; }
        @media print { body { padding: 16px; } }
      </style></head>
      <body>
        <h1>Declaración Renta (IRPF)</h1>
        <p class="sub">${selected?.nombre || ""} · Ejercicio ${anio} · Imputación ${pct}</p>
        <div class="stats">
          <div class="stat"><div class="stat-label">Ingresos a declarar (${pct})</div><div class="stat-value">${eur(data.ingresos_declarar)}</div><div class="stat-sub">Base fiscal: ${eur(data.total_ingresos)}</div></div>
          <div class="stat"><div class="stat-label">Gastos asociados (${pct})</div><div class="stat-value">${eur(data.gastos_declarar)}</div><div class="stat-sub">Total gastos año: ${eur(data.gastos_anio)}</div></div>
          <div class="stat"><div class="stat-label">Rendimiento neto</div><div class="stat-value">${eur(data.rendimiento_neto)}</div></div>
        </div>
        <h2>Ingresos por inquilino · ${anio}</h2>
        <table>
          <thead><tr>
            <th>Inquilino</th><th>DNI</th><th>Inicio contrato</th><th class="r">Días en el piso</th><th class="r">Ingresos fiscales</th><th class="r">A declarar (${pct})</th><th class="r">A declarar ajustado</th>
          </tr></thead>
          <tbody>
            ${(data.detalle || []).length === 0 ? `<tr><td colspan="7" style="text-align:center;padding:24px;color:#999">Sin ingresos registrados en ${anio}</td></tr>` :
              data.detalle.map(d => `
                <tr>
                  <td><strong>${d.nombre}</strong></td>
                  <td class="m">${d.dni || "—"}</td>
                  <td class="m">${d.fecha_inicio_contrato ? d.fecha_inicio_contrato.slice(0, 10) : "—"}</td>
                  <td class="r m">${d.dias_en_piso ?? 0}</td>
                  <td class="r m">${eur(d.ingresos)}</td>
                  <td class="r m">${eur(d.ingresos_declarar)}</td>
                  <td class="r m">${eur(d.ingresos_ajustados_declarar || 0)}</td>
                </tr>
                ${(d.meses || []).length > 0 ? `
                <tr class="month-detail">
                  <td colspan="7">
                    <table>
                      <thead><tr>
                        <th>Mes</th><th class="r">Días</th><th class="r">Base fiscal</th><th class="r">Ajustado a declarar (${pct})</th>
                      </tr></thead>
                      <tbody>
                        ${d.meses.map(m => `
                          <tr>
                            <td>${m.etiqueta || MESES_NOMBRE[m.mes - 1]}</td>
                            <td class="r m">${m.dias}</td>
                            <td class="r m">${eur(m.ingresos || 0)}</td>
                            <td class="r m">${eur((Number(m.ingresos_ajustados) || 0) * declarationFactor)}</td>
                          </tr>
                        `).join("")}
                        <tr class="total-row">
                          <td>TOTAL</td>
                          <td class="r m">${d.dias_en_piso ?? 0}</td>
                          <td class="r m">${eur(d.ingresos)}</td>
                          <td class="r m">${eur((d.meses || []).reduce((s, x) => s + (Number(x.ingresos_ajustados) || 0), 0) * declarationFactor)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>` : ""}
              `).join("")}
          </tbody>
        </table>
        <p class="footer">* Cálculo orientativo. Modo ${pct} imputa el ${modo === "50" ? "50% a cada uno de los dos propietarios" : "100% a un único propietario"}. Los gastos asociados incluyen suministros y gastos del año seleccionado. Las fianzas recibidas se excluyen de la base fiscal salvo que estén retenidas al finalizar el contrato.</p>
        <script>setTimeout(() => window.print(), 200);</script>
      </body>
      </html>
    `);
    w.document.close();
  };

  if (!selectedId)
    return <div className="data-card p-10 text-center text-muted-foreground">Selecciona o crea un piso primero.</div>;

  return (
    <div data-testid="irpf-page">
      <PageHeader title="Declaración Renta (IRPF)" subtitle={selected?.nombre}>
        <Button variant="outline" size="sm" onClick={printIrpf} className="no-print" data-testid="print-irpf-button">
          <Printer className="h-4 w-4 mr-1.5" /> Imprimir
        </Button>
      </PageHeader>

      <div className="flex flex-wrap gap-4 mb-6 no-print">
        <div>
          <p className="overline mb-1.5">Año fiscal</p>
          <Select value={anio} onValueChange={setAnio}>
            <SelectTrigger className="w-32" data-testid="irpf-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <p className="overline mb-1.5">Imputación</p>
          <div className="flex gap-2">
            {[["50", "50% (2 propietarios)"], ["100", "100% (1 propietario)"]].map(([k, l]) => (
              <button key={k} onClick={() => setModo(k)} data-testid={`irpf-modo-${k}`}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${modo === k ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground hover:text-foreground"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading || !data ? (
        <div className="data-card p-10 text-center text-muted-foreground">Calculando…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <StatCard label={`Ingresos a declarar (${modo}%)`} value={eur(data.ingresos_declarar)} icon={TrendingUp} accent="success" sub={`Base fiscal: ${eur(data.total_ingresos)}`} testid="irpf-ingresos" />
            <StatCard label={`Gastos asociados (${modo}%)`} value={eur(data.gastos_declarar)} icon={Receipt} accent="danger" sub={`Total gastos año: ${eur(data.gastos_anio)}`} testid="irpf-gastos" />
            <StatCard label="Rendimiento neto" value={eur(data.rendimiento_neto)} icon={Calculator} accent={data.rendimiento_neto >= 0 ? "primary" : "danger"} testid="irpf-rendimiento" />
          </div>

          <div className="data-card overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="font-display font-medium">Ingresos por inquilino · {anio}</h3>
            </div>
            <div className="p-4 border-b border-border flex items-center justify-between no-print">
              <p className="text-sm text-muted-foreground">Desglose mensual y días por contrato. Pulsa una fila para abrir o cerrar su detalle.</p>
              <button
                onClick={toggleAll}
                className="px-3 py-1.5 rounded-md text-sm font-medium transition-colors bg-card border border-border text-muted-foreground hover:text-foreground"
                data-testid="irpf-advanced-toggle"
              >
                {allExpanded ? "Contraer todo" : "Expandir todo"}
              </button>
            </div>
            <Table data-testid="irpf-table">
              <TableHeader><TableRow>
                <TableHead>Inquilino</TableHead>
                <TableHead className="hidden lg:table-cell">DNI</TableHead>
                <TableHead className="hidden lg:table-cell">Inicio contrato</TableHead>
                <TableHead className="hidden lg:table-cell text-right">Días en el piso ({anio})</TableHead>
                <TableHead className="text-right">Ingresos fiscales</TableHead>
                <TableHead className="text-right">A declarar ({modo}%)</TableHead>
                <TableHead className="text-right">A declarar ajustado</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.detalle.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Sin ingresos registrados en {anio}</TableCell></TableRow>
                ) : (
                  data.detalle.map((d) => (
                    <React.Fragment key={d.tenant_id}>
                      <TableRow
                        className="cursor-pointer"
                        onClick={() => toggleTenant(d.tenant_id)}
                        data-testid={`irpf-row-${d.tenant_id}`}
                      >
                        <TableCell className="font-medium">{d.nombre}</TableCell>
                        <TableCell className="hidden lg:table-cell font-mono text-sm">{d.dni || "—"}</TableCell>
                        <TableCell className="hidden lg:table-cell font-mono text-sm">{d.fecha_inicio_contrato ? d.fecha_inicio_contrato.slice(0, 10) : "—"}</TableCell>
                        <TableCell className="hidden lg:table-cell text-right font-mono">{d.dias_en_piso ?? 0}</TableCell>
                        <TableCell className="text-right font-mono">{eur(d.ingresos)}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">{eur(d.ingresos_declarar)}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">{eur(d.ingresos_ajustados_declarar || 0)}</TableCell>
                      </TableRow>

                      {openTenants.includes(d.tenant_id) && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-secondary/40">
                            <div className="py-2">
                              <p className="text-sm font-medium mb-2">Desglose mensual</p>
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-muted-foreground border-b border-border">
                                      <th className="text-left py-1">Mes</th>
                                      <th className="text-right py-1">Días</th>
                                      <th className="text-right py-1">Base fiscal</th>
                                      <th className="text-right py-1">Ajustado a declarar ({modo}%)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(d.meses || []).map((m) => (
                                      <tr key={m.key || m.mes} className="border-b border-border/60">
                                        <td className="py-1">{m.etiqueta || MESES_NOMBRE[m.mes - 1]}</td>
                                        <td className="text-right font-mono py-1">{m.dias}</td>
                                        <td className="text-right font-mono py-1">{eur(m.ingresos || 0)}</td>
                                        <td className="text-right font-mono py-1">{eur((Number(m.ingresos_ajustados) || 0) * declarationFactor)}</td>
                                      </tr>
                                    ))}
                                    <tr className="border-t border-border">
                                      <td className="py-2 font-medium">TOTAL</td>
                                      <td className="text-right font-mono py-2">{d.dias_en_piso ?? 0}</td>
                                      <td className="text-right font-mono font-semibold py-2">{eur(d.ingresos)}</td>
                                      <td className="text-right font-mono font-semibold py-2">
                                        {eur((d.meses || []).reduce((s, x) => s + (Number(x.ingresos_ajustados) || 0), 0) * declarationFactor)}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            * Cálculo orientativo. Modo {modo}% imputa el {modo === "50" ? "50% a cada uno de los dos propietarios" : "100% a un único propietario"}.
            Los gastos asociados incluyen suministros y gastos del año seleccionado. Las fianzas recibidas se excluyen de la base fiscal salvo que estén retenidas al finalizar el contrato.
          </p>
        </>
      )}
    </div>
  );
}
